import React, { useState, useRef } from 'react';
import { C } from '../utils/theme.js';
import { t } from '../utils/i18n.js';
import Card from './Card.jsx';

var STEPS = [
  { key: "license", icon: "\u{1F4DC}", color: C.a3, titleKey: "onboardingStep4Title", optional: false },
  { key: "step1", icon: "\u{1F30D}", color: "#a855f7", titleKey: "onboardingStep1Title", optional: true },
  { key: "step2", icon: "\u{1F4E5}", color: "#eab308", titleKey: "onboardingStep2Title", optional: true },
  { key: "step3", icon: "\u{1F4E1}", color: "#eab308", titleKey: "onboardingStep3Title", optional: true }
];

export default function OnboardingWizard(props) {
  var lang = props.lang;
  var authToken = props.authToken;
  var authUser = props.authUser;
  var onClose = props.onClose;
  var lotwLogin = props.lotwLogin;
  var setLotwLogin = props.setLotwLogin;
  var lotwPass = props.lotwPass;
  var setLotwPass = props.setLotwPass;
  var lotwStatus = props.lotwStatus;
  var setLotwStatus = props.setLotwStatus;
  var lotwMsg = props.lotwMsg;
  var setLotwMsg = props.setLotwMsg;
  var lotwLoading = props.lotwLoading;
  var setLotwLoading = props.setLotwLoading;
  var qsosCount = props.qsosCount || 0;

  // QRZ Logbook import state (local to onboarding)
  var qrzKeySt = useState(""); var qrzKey = qrzKeySt[0]; var setQrzKey = qrzKeySt[1];
  var qrzBusySt = useState(false); var qrzBusy = qrzBusySt[0]; var setQrzBusy = qrzBusySt[1];
  var qrzMsgSt = useState(""); var qrzMsg = qrzMsgSt[0]; var setQrzMsg = qrzMsgSt[1];
  var qrzDoneSt = useState(false); var qrzDone = qrzDoneSt[0]; var setQrzDone = qrzDoneSt[1];
  var qrzImportedSt = useState(0); var qrzImported = qrzImportedSt[0]; var setQrzImported = qrzImportedSt[1];

  function saveQrzAndImport() {
    if (!qrzKey.trim()) { setQrzMsg(t(lang, "fillQrzKey")); return; }
    setQrzBusy(true); setQrzMsg(t(lang, "validating"));
    fetch("/api/qrz/credentials", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + authToken },
      body: JSON.stringify({ apiKey: qrzKey.trim() })
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) { setQrzBusy(false); setQrzMsg(d.error); return; }
        setQrzMsg(t(lang, "qrzImporting"));
        fetch("/api/qrz/sync", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + authToken },
          body: JSON.stringify({ full: true })
        })
          .then(function(r) { return r.json(); })
          .then(function(s) {
            setQrzBusy(false);
            if (s.error) { setQrzMsg(s.error); return; }
            setQrzDone(true); setQrzImported(s.inserted || 0);
            setQrzMsg(t(lang, "syncOk") + " " + (s.inserted || 0) + " " + t(lang, "newQsos"));
          })
          .catch(function() { setQrzBusy(false); setQrzMsg(t(lang, "errConnection")); });
      })
      .catch(function() { setQrzBusy(false); setQrzMsg(t(lang, "errConnection")); });
  }

  // Open on the mandatory pending step (license) — index 0. If already verified, start on first optional.
  var initialStep = (authUser && authUser.licenseVerified) ? 1 : 0;
  var stepSt = useState(initialStep); var step = stepSt[0]; var setStep = stepSt[1];
  var copiedSt = useState(""); var copied = copiedSt[0]; var setCopied = copiedSt[1];

  // License verification state
  var licVerifiedSt = useState(!!(authUser && authUser.licenseVerified)); var licVerified = licVerifiedSt[0]; var setLicVerified = licVerifiedSt[1];
  var licBusySt = useState(false); var licBusy = licBusySt[0]; var setLicBusy = licBusySt[1];
  var licResultSt = useState(null); var licResult = licResultSt[0]; var setLicResult = licResultSt[1];
  var licFileRef = useRef(null);

  function verifyLicense(file) {
    if (!file || licBusy) return;
    setLicBusy(true); setLicResult(null);
    var fd = new FormData();
    fd.append("license", file);
    fetch("/api/onboarding/verify-license", {
      method: "POST",
      headers: { Authorization: "Bearer " + authToken },
      body: fd
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setLicBusy(false);
        if (d.verified) {
          setLicVerified(true);
          setLicResult({ ok: true, callsign: d.callsign, holder: d.holderName, cls: d.licenseClass, country: d.country, expiry: d.expiry });
        } else if (d.error) {
          setLicResult({ ok: false, reason: "error", msg: d.error });
        } else {
          setLicResult({ ok: false, reason: d.reason || "low_confidence", detected: d.detectedCallsign, expiry: d.expiry });
        }
      })
      .catch(function() { setLicBusy(false); setLicResult({ ok: false, reason: "error", msg: t(lang, "errConnection") }); });
  }

  function handleLicenseFile(e) {
    var files = e.target.files;
    if (files && files.length > 0) verifyLicense(files[0]);
  }

  function markComplete() {
    fetch("/api/onboarding/complete", {
      method: "PATCH",
      headers: { Authorization: "Bearer " + authToken }
    }).catch(function() {});
    onClose();
  }

  function copyToClipboard(text, id) {
    navigator.clipboard.writeText(text).then(function() {
      setCopied(id);
      setTimeout(function() { setCopied(""); }, 2000);
    });
  }

  // LoTW save handler
  function saveLotwCredentials() {
    if (!lotwLogin || !lotwPass) { setLotwMsg(t(lang, "fillLoginPass")); return; }
    setLotwLoading(true); setLotwMsg("");
    fetch("/api/lotw/credentials", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + authToken },
      body: JSON.stringify({ login: lotwLogin, password: lotwPass })
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setLotwLoading(false);
        if (d.error) { setLotwMsg(d.error); }
        else { setLotwMsg(t(lang, "onboardingLotwSaved")); setLotwStatus({ configured: true, login: lotwLogin, syncEnabled: true }); }
      })
      .catch(function() { setLotwLoading(false); setLotwMsg(t(lang, "errConnection")); });
  }

  // Step completed checks
  var lotwDone = (lotwStatus && lotwStatus.configured) || (authUser && authUser.hasLotw);
  var uploadDone = qsosCount > 0 || qrzDone;
  var licenseDone = licVerified;

  function renderStepIndicator() {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, margin: "24px 0 28px" }}>
        {STEPS.map(function(s, i) {
          var isActive = i === step;
          var isDone = (i === 0 && licenseDone) || (i === 1 && lotwDone) || (i === 2 && uploadDone) || (i === 3 && copied === "finished");
          var isRequiredPending = !s.optional && !isDone;
          var dotBg = isActive ? C.a1 : isDone ? C.a2 : isRequiredPending ? C.a3 : C.s3;
          var dotColor = isActive || isDone || isRequiredPending ? "#000" : C.t3;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div style={{ width: 48, height: 2, background: isDone || isActive ? C.a1 + "40" : C.bd }} />
              )}
              <div
                onClick={function() { setStep(i); }}
                className={isRequiredPending && !isActive ? "onb-required-pulse" : ""}
                style={{
                  width: 38, height: 38, borderRadius: "50%",
                  background: dotBg, color: dotColor,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 14, cursor: "pointer",
                  border: isActive ? "2px solid " + C.a1 : isRequiredPending ? "2px solid " + C.a3 : "2px solid transparent",
                  transition: "all 0.2s"
                }}
              >
                {isDone ? "\u2713" : i + 1}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  function renderStep0() {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28 }}>{"\u{1F30D}"}</span>
          <div>
            <div style={{ fontWeight: 700, color: "#a855f7", fontSize: 16 }}>{t(lang, "onboardingStep1Title")}</div>
            <div style={{ fontSize: 11, color: C.t3 }}>{t(lang, "onboardingOptional")}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.t2, marginBottom: 16, lineHeight: 1.6 }}>
          {t(lang, "onboardingStep1Desc")}
        </div>

        {lotwDone ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: C.a2 + "15", border: "1px solid " + C.a2 + "40", borderRadius: 10 }}>
            <span style={{ fontSize: 22 }}>{"\u2705"}</span>
            <div>
              <div style={{ color: C.a2, fontWeight: 700, fontSize: 13 }}>{t(lang, "onboardingStepComplete")}</div>
              {lotwStatus && lotwStatus.login && <div style={{ color: C.t3, fontSize: 11 }}>Login: <b style={{ color: "#a855f7" }}>{lotwStatus.login}</b></div>}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              value={lotwLogin}
              onChange={function(e) { setLotwLogin(e.target.value); }}
              placeholder={t(lang, "lotwLoginPh")}
              style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 12px", color: C.a1, fontSize: 13, fontFamily: "monospace", width: "100%", boxSizing: "border-box" }}
            />
            <input
              value={lotwPass}
              onChange={function(e) { setLotwPass(e.target.value); }}
              placeholder={t(lang, "lotwPassPh")}
              type="password"
              style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 12px", color: C.a1, fontSize: 13, fontFamily: "monospace", width: "100%", boxSizing: "border-box" }}
            />
            <button
              onClick={saveLotwCredentials}
              disabled={lotwLoading}
              style={{ background: "#a855f7", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, opacity: lotwLoading ? 0.6 : 1 }}
            >
              {lotwLoading ? t(lang, "saving") : t(lang, "saveLotwCred")}
            </button>
          </div>
        )}

        {lotwMsg && (
          <div style={{
            marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12,
            color: lotwMsg.indexOf("Erro") > -1 || lotwMsg.indexOf("Error") > -1 ? "#f43f5e" : C.a2,
            background: lotwMsg.indexOf("Erro") > -1 || lotwMsg.indexOf("Error") > -1 ? "#f43f5e10" : C.a2 + "10"
          }}>
            {lotwMsg}
          </div>
        )}
      </div>
    );
  }

  function renderStep1() {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28 }}>{"\u{1F4E5}"}</span>
          <div>
            <div style={{ fontWeight: 700, color: "#eab308", fontSize: 16 }}>{t(lang, "onboardingStep2Title")}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.t2, marginBottom: 16, lineHeight: 1.6 }}>
          {t(lang, "qrzImportDesc")}
        </div>

        {qrzDone ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: C.a2 + "15", border: "1px solid " + C.a2 + "40", borderRadius: 10 }}>
            <span style={{ fontSize: 22 }}>{"\u2705"}</span>
            <div>
              <div style={{ color: C.a2, fontWeight: 700, fontSize: 13 }}>{t(lang, "onboardingStepComplete")}</div>
              <div style={{ color: C.t3, fontSize: 11 }}>{qrzImported} QSOs {t(lang, "qsosViaQrz")}</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              value={qrzKey}
              onChange={function(e) { setQrzKey(e.target.value); }}
              placeholder={t(lang, "qrzApiKeyPh")}
              disabled={qrzBusy}
              style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 12px", color: "#eab308", fontSize: 13, fontFamily: "monospace", width: "100%", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#eab30814", border: "1px solid #eab30840", borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontSize: 16, lineHeight: 1.3 }}>{"\u{1F4A1}"}</span>
              <span style={{ fontSize: 12, color: "#eab308", lineHeight: 1.55, fontWeight: 600 }}>{t(lang, "qrzApiKeyHint")}</span>
            </div>
            <button
              onClick={saveQrzAndImport}
              disabled={qrzBusy}
              style={{ background: "#eab308", color: "#2a2300", border: "none", padding: "10px 20px", borderRadius: 8, cursor: qrzBusy ? "default" : "pointer", fontSize: 13, fontWeight: 800, opacity: qrzBusy ? 0.6 : 1 }}
            >
              {qrzBusy ? t(lang, "qrzImporting") : t(lang, "qrzImportBtn")}
            </button>
          </div>
        )}

        {qrzMsg && (
          <div style={{
            marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12,
            color: qrzMsg.indexOf("Erro") > -1 || qrzMsg.indexOf("Error") > -1 || qrzMsg.indexOf("inválida") > -1 || qrzMsg.indexOf("invalid") > -1 ? "#f43f5e" : C.a2,
            background: qrzMsg.indexOf("Erro") > -1 || qrzMsg.indexOf("Error") > -1 || qrzMsg.indexOf("inválida") > -1 || qrzMsg.indexOf("invalid") > -1 ? "#f43f5e10" : C.a2 + "10"
          }}>
            {qrzMsg}
          </div>
        )}
      </div>
    );
  }

  function renderStep2() {
    var iframeCode = '<iframe src="https://hamexpert.cloud/qrz/embed/' + authUser.callsign + '" width="100%" height="600" style="border:none;border-radius:14px;" scrolling="no"></iframe>';
    var badgeCode = '<a href="https://hamexpert.cloud/qrz/embed/' + authUser.callsign + '" target="_blank"><img src="https://img.shields.io/badge/HamExpert-' + authUser.callsign + '-00d4ff?style=for-the-badge" alt="HamExpert" /></a>';

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28 }}>{"\u{1F4E1}"}</span>
          <div>
            <div style={{ fontWeight: 700, color: "#eab308", fontSize: 16 }}>{t(lang, "onboardingStep3Title")}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.t2, marginBottom: 16, lineHeight: 1.6 }}>
          {t(lang, "onboardingStep3Desc")}
        </div>

        <div style={{ fontSize: 11, color: C.t3, marginBottom: 6 }}>{"\u{1F517}"} <b style={{ color: C.a1 }}>{t(lang, "publicLink")}:</b></div>
        <div style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#eab308", fontFamily: "monospace", wordBreak: "break-all", marginBottom: 12 }}>
          {"https://hamexpert.cloud/qrz/embed/" + authUser.callsign}
        </div>

        <div style={{ fontSize: 11, color: C.t3, marginBottom: 6 }}>{"\u{1F4CB}"} <b style={{ color: C.a1 }}>{t(lang, "htmlCode")}:</b></div>
        <div style={{ background: C.s2, border: "1px solid " + C.bd, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: C.a2, fontFamily: "monospace", wordBreak: "break-all", lineHeight: 1.6, marginBottom: 12, whiteSpace: "pre-wrap" }}>
          {iframeCode}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={function() { copyToClipboard(iframeCode, "iframe"); }}
            style={{ background: "#eab30820", color: "#eab308", border: "1px solid #eab30850", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            {copied === "iframe" ? "\u2705 " + t(lang, "onboardingCopied") : "\u{1F4CB} " + t(lang, "copyIframe")}
          </button>
          <button
            onClick={function() { copyToClipboard(badgeCode, "badge"); }}
            style={{ background: C.a1 + "20", color: C.a1, border: "1px solid " + C.a1 + "50", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            {copied === "badge" ? "\u2705 " + t(lang, "onboardingCopied") : "\u{1F3F7}\uFE0F " + t(lang, "copyBadge")}
          </button>
        </div>

        <div style={{ marginTop: 14, fontSize: 10, color: C.t3, lineHeight: 1.5 }}>
          {"\u{1F4A1}"} <b>{t(lang, "tip")}:</b> {t(lang, "qrzTip")}
        </div>
      </div>
    );
  }

  function renderStepLicense() {
    var reasonMsg = "";
    if (licResult && !licResult.ok) {
      if (licResult.reason === "error") reasonMsg = licResult.msg;
      else if (licResult.reason === "not_license") reasonMsg = t(lang, "onbLicenseReasonNotLicense");
      else if (licResult.reason === "callsign_mismatch") reasonMsg = t(lang, "onbLicenseReasonMismatch") + (licResult.detected ? " (" + licResult.detected + ")" : "");
      else if (licResult.reason === "expired") reasonMsg = t(lang, "onbLicenseReasonExpired") + (licResult.expiry ? " (" + licResult.expiry + ")" : "");
      else reasonMsg = t(lang, "onbLicenseReasonLowConf");
    }

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28 }}>{"\u{1F4DC}"}</span>
          <div>
            <div style={{ fontWeight: 700, color: C.a3, fontSize: 16 }}>{t(lang, "onbLicenseTitle")}</div>
            <div style={{ fontSize: 11, color: C.t3 }}>{t(lang, "onbLicensePrivacy")}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.t2, marginBottom: 16, lineHeight: 1.6 }}>
          {t(lang, "onbLicenseDesc")}
        </div>

        {licVerified ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", background: C.a2 + "15", border: "1px solid " + C.a2 + "40", borderRadius: 10 }}>
            <span style={{ fontSize: 22 }}>{"\u2705"}</span>
            <div>
              <div style={{ color: C.a2, fontWeight: 700, fontSize: 13 }}>{t(lang, "onbLicenseVerified")}</div>
              <div style={{ color: C.t3, fontSize: 11 }}>
                {licResult && licResult.ok && licResult.holder ? licResult.holder + " \u2022 " : ""}
                <b style={{ color: C.a3 }}>{(licResult && licResult.ok && licResult.callsign) || (authUser && authUser.callsign)}</b>
                {licResult && licResult.ok && licResult.cls ? " \u2022 " + licResult.cls : ""}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{ padding: 28, textAlign: "center", cursor: licBusy ? "default" : "pointer", border: "2px dashed " + C.bd, borderRadius: 12, transition: "border-color 0.2s", opacity: licBusy ? 0.7 : 1 }}
              onClick={function() { if (!licBusy && licFileRef.current) licFileRef.current.click(); }}
            >
              <input ref={licFileRef} type="file" accept="image/*" onChange={handleLicenseFile} style={{ display: "none" }} />
              {licBusy ? (
                <React.Fragment>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>{"\u{1F50D}"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.a3 }}>{t(lang, "onbLicenseVerifying")}</div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>{"\u{1F4F7}"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{t(lang, "onbLicenseSelect")}</div>
                  <div style={{ fontSize: 11, color: C.t3, marginTop: 6 }}>JPG / PNG / WEBP</div>
                </React.Fragment>
              )}
            </div>

            {licResult && !licResult.ok && (
              <div style={{ marginTop: 12, padding: "12px 14px", background: C.a5 + "12", border: "1px solid " + C.a5 + "40", borderRadius: 10 }}>
                <div style={{ color: C.a5, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{"\u26A0\uFE0F " + t(lang, "onbLicenseFailTitle")}</div>
                <div style={{ color: C.t2, fontSize: 12, lineHeight: 1.5 }}>{reasonMsg}</div>
                <div style={{ color: C.t3, fontSize: 11, marginTop: 6 }}>{t(lang, "onbLicenseReview")}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  var stepRenderers = [renderStepLicense, renderStep0, renderStep1, renderStep2];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.85)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20
    }}>
      <div style={{
        background: "linear-gradient(135deg," + C.s1 + "," + C.bg + ")",
        border: "1px solid " + C.bd,
        borderRadius: 16,
        padding: "32px 28px",
        maxWidth: 580, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        position: "relative"
      }}>
        {/* Close button */}
        <button
          onClick={markComplete}
          style={{
            position: "absolute", top: 14, right: 14,
            background: "none", border: "none", color: C.t3,
            fontSize: 20, cursor: "pointer", padding: 4, lineHeight: 1
          }}
          title={t(lang, "onboardingSkipAll")}
        >
          {"\u2715"}
        </button>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{"\u{1F3AF}"}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.t1 }}>
            {t(lang, "onboardingTitle")}
          </div>
          <div style={{ fontSize: 13, color: C.t2, marginTop: 6 }}>
            {t(lang, "onboardingSubtitle")}
          </div>
          <div style={{ fontSize: 12, color: C.a1, fontWeight: 600, marginTop: 4 }}>
            {authUser.callsign}
          </div>
        </div>

        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Step title bar */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, marginBottom: 16
        }}>
          <span style={{ fontSize: 11, color: C.t3, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
            {t(lang, STEPS[step].titleKey)}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, textTransform: "uppercase", letterSpacing: 0.5,
            background: STEPS[step].optional ? C.t3 + "22" : C.a3 + "22",
            color: STEPS[step].optional ? C.t3 : C.a3,
            border: "1px solid " + (STEPS[step].optional ? C.t3 + "44" : C.a3 + "55")
          }}>
            {STEPS[step].optional ? t(lang, "onboardingOptionalTag") : t(lang, "onboardingRequiredTag")}
          </span>
        </div>

        {/* Step content */}
        <Card style={{ padding: 22, marginBottom: 20 }}>
          {stepRenderers[step]()}
        </Card>

        {/* Footer buttons */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            {step > 0 && (
              <button
                onClick={function() { setStep(step - 1); }}
                style={{
                  background: C.a1 + "15", color: C.a1,
                  border: "1px solid " + C.a1 + "30",
                  padding: "10px 20px", borderRadius: 8,
                  cursor: "pointer", fontSize: 13, fontWeight: 600
                }}
              >
                {"\u2190 " + t(lang, "onboardingPrev")}
              </button>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {step < STEPS.length - 1 && (
              <button
                onClick={function() { setStep(step + 1); }}
                style={{
                  background: C.s2, color: C.t2,
                  border: "1px solid " + C.bd,
                  padding: "10px 18px", borderRadius: 8,
                  cursor: "pointer", fontSize: 13, fontWeight: 600
                }}
              >
                {t(lang, "onboardingNextOptional") + " \u2192"}
              </button>
            )}
            <button
              onClick={markComplete}
              style={{
                background: C.a2, color: "#000",
                border: "none",
                padding: "10px 24px", borderRadius: 8,
                cursor: "pointer", fontSize: 13, fontWeight: 700
              }}
            >
              {"\u{1F680} " + t(lang, "onboardingFinish")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
